import React, { Fragment, useState } from 'react';
import { bool, func, object, string } from 'prop-types';
import { intlShape } from 'react-intl';
import classNames from 'classnames';
import { path, pathOr } from 'ramda';

import { Form as FinalForm, FormSpy } from 'react-final-form';
import * as validators from '../../../util/validators';
import { types as sdkTypes } from '../../../util/sdkLoader';
import { convertMoneyToNumber, formatMoney } from '../../../util/currency';
import { BOOKING_DATE, END_TIME, START_TIME, PROPOSAL_PRICE } from '../../../constants/booking';
import { propTypes } from '../../../util/types';
import {
  DiscountBanner,
  FieldDateAndTimeInput,
  FieldParticipants,
  FieldStepperEditable,
  Form,
  PrimaryButton,
  SecondaryButton,
} from '../../../components';
import { moment } from '../../../util/dates';
import {
  getBaseTotalFromCustomerTotal,
  getMinTotalAmountPerBookingWithCustomerFee,
} from '../../../util/pricing';

import css from './BookingForm.module.css';

const { Money } = sdkTypes;

const BookingForm = (props) => {
  const {
    className,
    formattedPrice,
    estimatedPrice,
    exposeFormValues,
    proposalPriceMin,
    initialValues,
    isSameSearchBookingParamsAsCurrentBookingParams,
    intl,
    isOwnListing,
    listingId,
    monthlyTimeSlots,
    onFetchTimeSlots,
    onSubmit,
    publicData,
    timeZone,
    isInCart,
    hasItemsInCart,
    applyInstantBooking,
  } = props;
  const {
    enfantsBienvenue: childrenAllowed,
    maxCapacityToGuest = 0,
    minimumHoursPerBooking = 1,
    minHoursBeforeBookingRequest,
    pricingExtraHours,
  } = publicData;
  const [isMakingAnOffer, setIsMakingAnOffer] = useState(false);
  const formatedProposalPriceMin = formatMoney(intl, proposalPriceMin);
  const formatedEstimatedPrice = formatMoney(intl, estimatedPrice);
  const proposalPricePlaceholder = intl.formatMessage({
    id: 'BookingPanel.BookingForm.enterProposalAmount',
  });
  const estimatedPriceValue = estimatedPrice && convertMoneyToNumber(estimatedPrice);
  const proposalPriceMinValue = proposalPriceMin && convertMoneyToNumber(proposalPriceMin);
  const minPriceErrorText = intl.formatMessage(
    { id: 'BookingPanel.BookingForm.proposalInvalid' },
    { price: formatedProposalPriceMin }
  );
  const priceValidators =
    !!proposalPriceMin && validators.minNumberAsString(proposalPriceMinValue, minPriceErrorText);
  const discountText = intl.formatMessage(
    { id: 'BookingPanel.BookingForm.discount' },
    {
      percentage: path(['variation'], pricingExtraHours),
    }
  );
  const proposalSubmitText =
    proposalPriceMin &&
    intl.formatMessage(
      {
        id: `BookingPanel.BookingForm.submitWithProposal`,
      },
      { price: formattedPrice }
    );
  const submitText = intl.formatMessage(
    {
      id: `BookingPanel.BookingForm.${!formattedPrice ? 'submitWithoutPrice' : 'submitWithPrice'}`,
    },
    { price: formattedPrice }
  );
  const selectButtonText = intl.formatMessage({ id: 'BookingPanel.selectButton' });
  const seeOrderText = intl.formatMessage({ id: 'BookingPanel.SeeOrder' });
  const classes = classNames(css.root, className);
  const minTotalAmountPerBooking = pathOr('0', ['minTotalAmountPerBooking'], publicData);

  return (
    <FinalForm
      initialValues={initialValues}
      onSubmit={(values) => onSubmit(values)}
      render={(formRenderProps) => {
        const { active, form, handleSubmit, values } = formRenderProps;
        const { bookingDate, startTime, endTime, adults, children, proposalPrice } = values;
        const proposalPriceValue = parseInt(proposalPrice || '0', 10);
        const isProposalPriceValid =
          !proposalPriceMin ||
          (!!proposalPriceMin.amount && proposalPriceValue >= proposalPriceMinValue);
        const now = new Date();

        const startMoment =
          bookingDate && startTime
            ? new Date(
                `${moment(bookingDate).format('YYYY-MM-DD')}T${startTime}:00`
              )
            : null;

        const diffHours = startMoment
          ? (startMoment.getTime() - now.getTime()) / (1000 * 60 * 60)
          : null;

        const parsedMinHoursForListingParam = parseInt(minHoursBeforeBookingRequest, 10);

        const minHoursForListingParam =
          Number.isFinite(parsedMinHoursForListingParam) && parsedMinHoursForListingParam > 0
            ? parsedMinHoursForListingParam
            : 1;

        const isTooLastMinuteForListingParam =
          diffHours !== null && diffHours < minHoursForListingParam;
        const canSubmit =
          bookingDate && startTime && endTime && adults >= 1 && !isTooLastMinuteForListingParam;

        const guestCount = (adults || 0) + (children || 0);
        const minTotalCustomerPayinCents = getMinTotalAmountPerBookingWithCustomerFee(
          minTotalAmountPerBooking,
          guestCount
        );

        const exceedMinimumTotalAmountBooking = (() => {
          try {
            const minTotalAmountPerBookingInt = parseInt(minTotalAmountPerBooking, 10);
            if (!minTotalAmountPerBookingInt || minTotalAmountPerBookingInt <= 0) return true;

            const minTotalAmountPerBookingCents = minTotalAmountPerBookingInt * 100;
            const payinTotal = pathOr(0, ['amount'], estimatedPrice);
            const baseTotal = getBaseTotalFromCustomerTotal(payinTotal, guestCount);
            return baseTotal >= minTotalAmountPerBookingCents;
          } catch (e) {
            return true;
          }
        })();
        const handleClickWithoutValues = (e) => {
          if (!bookingDate) {
            form.focus(BOOKING_DATE);
          } else if (!startTime) {
            form.focus(START_TIME);
          } else if (!endTime) {
            form.focus(END_TIME);
          } else if (proposalPriceMin && !isProposalPriceValid) {
            form.focus(PROPOSAL_PRICE);
          }
        };

        return (
          <Fragment>
            <Form className={classes} id="bookingForm" onSubmit={handleSubmit}>
              <FormSpy
                subscription={{ values: true }}
                onChange={(state) => exposeFormValues(state.values)}
              />
              {!!(monthlyTimeSlots && timeZone) && (
                <div className={css.dateTimeContainer}>
                  <FieldDateAndTimeInput
                    className={css.fieldDateAndTimeInput}
                    activeField={active}
                    finalFormApi={form}
                    formValues={values}
                    listingId={listingId}
                    minimumHoursPerBooking={minimumHoursPerBooking}
                    monthlyTimeSlots={monthlyTimeSlots}
                    onFetchTimeSlots={onFetchTimeSlots}
                    timeZone={timeZone}
                  />
                  {pricingExtraHours && (
                    <DiscountBanner className={css.discountBanner} text={discountText} />
                  )}
                </div>
              )}
              <FieldParticipants
                className={css.fieldParticipants}
                adultsValue={adults}
                childrenAllowed={childrenAllowed}
                childrenValue={children}
                maxParticipants={maxCapacityToGuest}
              />
              <div className={css.submitButtonContainer}>
                {!!proposalPriceMin && isMakingAnOffer && (
                  <FieldStepperEditable
                    className={css.fieldStepper}
                    inputClassName={css.fieldStepperInput}
                    name="proposalPrice"
                    label={intl.formatMessage({
                      id: `BookingPanel.BookingForm.proposalPriceLabel`,
                    })}
                    initialValue={estimatedPriceValue}
                    min={proposalPriceMinValue}
                    placeholder={proposalPricePlaceholder}
                    validate={priceValidators}
                  />
                )}

                {formattedPrice &&
                  hasItemsInCart &&
                  (!isInCart || !isSameSearchBookingParamsAsCurrentBookingParams) && (
                    <div className={css.priceTotalPreview}>{formattedPrice}</div>
                  )}

                {proposalSubmitText && !isMakingAnOffer && (
                  <SecondaryButton
                    className={css.submitButton}
                    type={'button'}
                    onClick={() => setIsMakingAnOffer(true)}
                    disabled={isOwnListing}
                  >
                    {intl.formatMessage(
                      {
                        id: `BookingPanel.BookingForm.MakeAnOffer`,
                      },
                      { price: formattedPrice }
                    )}
                  </SecondaryButton>
                )}
                {proposalSubmitText && isMakingAnOffer && (
                  <PrimaryButton
                    className={css.submitButton}
                    type={canSubmit ? 'submit' : 'button'}
                    {...(!canSubmit && { onClick: handleClickWithoutValues })}
                    disabled={isOwnListing}
                  >
                    {proposalSubmitText}
                  </PrimaryButton>
                )}
                {!isMakingAnOffer && (
                  <PrimaryButton
                    className={css.submitButton}
                    type={canSubmit ? 'submit' : 'button'}
                    {...(!canSubmit && { onClick: handleClickWithoutValues })}
                    disabled={
                      isOwnListing ||
                      !exceedMinimumTotalAmountBooking ||
                      isTooLastMinuteForListingParam
                    }
                  >
                    {!applyInstantBooking &&
                      isInCart &&
                      isSameSearchBookingParamsAsCurrentBookingParams &&
                      seeOrderText}
                    {!applyInstantBooking &&
                      !isInCart &&
                      isSameSearchBookingParamsAsCurrentBookingParams &&
                      hasItemsInCart &&
                      selectButtonText}
                    {(applyInstantBooking ||
                      (!isInCart && !hasItemsInCart) ||
                      !isSameSearchBookingParamsAsCurrentBookingParams) &&
                      submitText}
                  </PrimaryButton>
                )}
                {isTooLastMinuteForListingParam && (
                  <p className={css.lastMinuteError}>
                    {intl.formatMessage(
                      { id: 'BookingPanel.BookingForm.tooLastMinuteForListingParam' },
                      { hours: minHoursForListingParam }
                    )}
                  </p>
                )}
                {!exceedMinimumTotalAmountBooking && estimatedPrice?.currency && (
                  <>
                    <p className={css.minTotalAmountRequired}>
                      {intl.formatMessage(
                        {
                          id: `BookingPanel.BookingForm.CheckoutMinTotalAmountRequired`,
                        },
                        {
                          amount: formatMoney(
                            intl,
                            new Money(minTotalCustomerPayinCents, estimatedPrice?.currency),
                            true
                          ),
                        }
                      )}
                    </p>
                  </>
                )}
              </div>
            </Form>
          </Fragment>
        );
      }}
    />
  );
};

BookingForm.defaultProps = {
  className: null,
  estimatedPrice: null,
  proposalPriceMin: null,
  initialValues: null,
  isOwnListing: false,
  listingId: null,
  monthlyTimeSlots: null,
};

BookingForm.propTypes = {
  className: string,
  exposeFormValues: func.isRequired,
  estimatedPrice: object,
  proposalPriceMin: object,
  initialValues: object,
  intl: intlShape.isRequired,
  isOwnListing: bool,
  listingId: propTypes.uuid,
  monthlyTimeSlots: object,
  onFetchTimeSlots: func.isRequired,
  onSubmit: func.isRequired,
  publicData: object.isRequired,
};

export default BookingForm;
